import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import MailIcon from '@mui/icons-material/Mail';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

const UserManagementPermissions = ({ 
  permissions, 
  roleForm, 
  setRoleForm 
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    active_users: false,
    invitations: false,
    roles_permissions: false
  });
  
  // User Management pages mapping
  const userManagementPages = [
    'active_users', 
    'invitations', 
    'roles_permissions'
  ];
  
  const pageNameMapping = {
    'active_users': 'Active Users Page',
    'invitations': 'Invitations Page',
    'roles_permissions': 'Roles & Permissions Page'
  };
  
  const pageIconMapping = {
    'active_users': <PeopleIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'invitations': <MailIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'roles_permissions': <AdminPanelSettingsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderUserManagementPagePermissions = (pageKey, pageName) => {
    const isExpanded = expandedSections[pageKey] !== false;
    let pagePermissions = [];
    
    if (pageKey === 'active_users') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.user_management.active_users'));
    } else if (pageKey === 'invitations') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.user_management.invitations'));
    } else if (pageKey === 'roles_permissions') {
      pagePermissions = permissions.filter(p => p.name && p.name.includes('settings.user_management.roles_permissions'));
    }

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view') && !p.name?.includes('.approve') && !p.name?.includes('.delete_permanent'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deactivatePerm = pagePermissions.find(p => p.name?.includes('.deactivate'));
    const completeRemovalPerm = pagePermissions.find(p => p.name?.includes('.complete_removal'));
    const invitePerm = pagePermissions.find(p => p.name?.includes('.invite'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete') && !p.name?.includes('.complete_removal'));

    return (
      <Box 
        key={pageKey}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [pageKey]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()}
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) {
                      newPermissions[viewPerm.id] = {};
                    }
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Active Users special permissions */}
            {pageKey === 'active_users' && (
              <>
                {/* Edit Permission */}
                {editPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Edit
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can edit active users
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[editPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[editPerm.id]) {
                          newPermissions[editPerm.id] = {};
                        }
                        newPermissions[editPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}

                {/* Deactivate Users Permission */}
                {deactivatePerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: completeRemovalPerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Deactivate Users
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can deactivate user accounts
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[deactivatePerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[deactivatePerm.id]) {
                          newPermissions[deactivatePerm.id] = {};
                        }
                        newPermissions[deactivatePerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}

                {/* Complete Removal Permission */}
                {completeRemovalPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Complete Removal
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can permanently remove users
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[completeRemovalPerm.id]?.delete || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[completeRemovalPerm.id]) {
                          newPermissions[completeRemovalPerm.id] = {};
                        }
                        newPermissions[completeRemovalPerm.id].delete = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}

            {/* Invitations special permissions */}
            {pageKey === 'invitations' && invitePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Invite Users
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can send user invitations
                  </Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[invitePerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[invitePerm.id]) {
                      newPermissions[invitePerm.id] = {};
                    }
                    newPermissions[invitePerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {/* Roles & Permissions special permissions */}
            {pageKey === 'roles_permissions' && (
              <>
                {/* Edit Permission */}
                {editPerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1,
                    borderBottom: deletePerm ? `1px solid ${alpha(theme.palette.grey[300], 0.5)}` : 'none'
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Edit
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can edit roles and permissions
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[editPerm.id]?.write || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[editPerm.id]) {
                          newPermissions[editPerm.id] = {};
                        }
                        newPermissions[editPerm.id].write = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}

                {/* Delete Permission */}
                {deletePerm && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    py: 1
                  }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Delete
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Can delete roles
                      </Typography>
                    </Box>
                    <Switch
                      checked={roleForm.permissions[deletePerm.id]?.delete || false}
                      onChange={(e) => {
                        const newPermissions = { ...roleForm.permissions };
                        if (!newPermissions[deletePerm.id]) {
                          newPermissions[deletePerm.id] = {};
                        }
                        newPermissions[deletePerm.id].delete = e.target.checked;
                        setRoleForm({ ...roleForm, permissions: newPermissions });
                      }}
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {userManagementPages.map(pageKey => {
        const pageName = pageNameMapping[pageKey] || pageKey;
        return renderUserManagementPagePermissions(pageKey, pageName);
      })}
    </Box>
  );
};

export default UserManagementPermissions;
